import { z } from 'zod/v4';

export const AdminWebshopsListSortColumns = [
  'shopId',
  'companyId',
  'name',
  'shopPlatform',
  'updatedAt',
  'isBlocked',
] as const;

export type AdminWebshopsListSortColumn =
  (typeof AdminWebshopsListSortColumns)[number];

const sortColumns = AdminWebshopsListSortColumns.join('|');

// One or more comma-separated `<column>:<ASC|DESC>` terms, e.g.
// `name:DESC,isBlocked:ASC` — the same grammar the legacy route accepted.
const sortPattern = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

// Legacy also took `isDropDown` (every row, unpaginated, no company filter) and
// `hasCustomCss`. Neither frontend sent them, and `hasCustomCss` — also a
// `filter` key — read a `custom_css` column the table does not have, so it could
// only ever have failed. Both are left out; an unknown param is ignored.
export const AdminWebshopsListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(sortPattern).optional(),
  // Case-insensitive substring match on the webshop name.
  q: z.string().max(255).optional(),
  // JSON object keyed by `shopId`, `companyId`, `name`, `shopPlatform` (each a
  // case-insensitive substring match) or `isBlocked` (0/1, exact). Malformed
  // JSON and unknown keys are ignored, not errors.
  filter: z.string().max(512).optional(),
});

export type AdminWebshopsListQuery = z.infer<
  typeof AdminWebshopsListQuerySchema
>;

// What the roster tables read. Legacy answered `SELECT *`-ish: every audit id
// column and a per-row `count` of the whole result set besides.
export const AdminWebshopListRowSchema = z.object({
  id: z.string(),
  companyId: z.string().nullable(),
  // Joined from the company, which may be a removed one — see `isActiveCompany`.
  companyName: z.string().nullable(),
  name: z.string().nullable(),
  shopPlatform: z.string().nullable(),
  // Comma-separated allowed-origin ids, not an array — the column stores one string.
  connectedOrigins: z.string().nullable(),
  // 0/1 out of Postgres, not a boolean.
  isBlocked: z.number().nullable(),
  // 1 when the attached company is still active, 0 when it was removed.
  isActiveCompany: z.number(),
  updatedAt: z.coerce.date().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
});

export type AdminWebshopListRow = z.infer<typeof AdminWebshopListRowSchema>;

// The platform's paginated envelope. `pagination.page` is 1-based, unlike the
// 0-based `page` query param. Legacy sent `{ data, meta: { count, page,
// pageSize, totalCount } }`.
export const AdminWebshopsListResponseSchema = z.object({
  metadata: z.object({
    success: z.boolean(),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  }),
  data: z.array(AdminWebshopListRowSchema),
});

export type AdminWebshopsListResponse = z.infer<
  typeof AdminWebshopsListResponseSchema
>;
